import { useCallback, useEffect, useMemo, useState } from "react";
import Button from "../../../../shared/components/Button";
import InfoTooltip from "../../../../shared/components/InfoTooltip";
import useBodyScrollLock from "../../../../shared/hooks/useBodyScrollLock";
import brandLogo from "../../../../assets/images/tnp-logo-neon.png";
import femaleAvatar from "../../../../assets/images/avatars/tnp-avatar-female.png";
import maleAvatar from "../../../../assets/images/avatars/tnp-avatar-male.png";
import { siteTexts } from "../../../../shared/i18n/siteTexts";
import { useSiteLanguage } from "../../../../shared/i18n/language";
import {
  getEffectiveIdentityColors,
  type OrganizerVisualIdentity,
} from "../../../../shared/utils/organizerVisualIdentity";
import { Super8RankingRow } from "../../shared/types/super8Ranking";
import "./RankingShareModal.css";

type RankingShareModalProps = {
  open: boolean;
  rankingName: string;
  categoryLabel: string;
  selectionLabel?: string;
  rankingRows: Super8RankingRow[];
  shareUrl: string;
  visualIdentity?: OrganizerVisualIdentity | null;
  onClose: () => void;
};

type SharePreview = {
  blob: Blob;
  filename: string;
  url: string;
};

type ShareAthlete = {
  position: number;
  playerName: string;
  points: number;
  avatarUrl?: string;
  sex?: "male" | "female" | "other" | "";
};

function initialsFromName(value?: string | null) {
  const initials = String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((chunk) => chunk[0]?.toUpperCase() || "")
    .join("");
  return initials || "TN";
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function blobFromCanvas(canvas: HTMLCanvasElement) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error(siteTexts.ranking.shareModal.canvas.imageError));
    }, "image/png");
  });
}

function loadShareImage(src?: string | null) {
  const safeSrc = String(src || "").trim();
  if (!safeSrc) return Promise.resolve<HTMLImageElement | null>(null);
  return new Promise<HTMLImageElement | null>((resolve) => {
    const image = new Image();
    if (!safeSrc.startsWith("data:")) image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = safeSrc;
  });
}

function defaultRankingAvatarUrl(sex?: ShareAthlete["sex"] | null) {
  return sex === "female" ? femaleAvatar : maleAvatar;
}

function formatRankingShareTitle(categoryLabel: string, selectionLabel?: string) {
  const safeSelection = String(selectionLabel || "").trim();
  if (safeSelection) return `Ranking ${safeSelection}`.trim();
  const categoryPrefix = siteTexts.ranking.page.labels.category;
  const safeCategory = String(categoryLabel || "").trim();
  const categoryText = safeCategory
    .toLowerCase()
    .startsWith(categoryPrefix.toLowerCase())
    ? safeCategory
    : `${categoryPrefix} ${safeCategory}`;
  return `Ranking ${categoryText}`.trim();
}

async function loadRankingAvatarImage(athlete: ShareAthlete) {
  const customImage = await loadShareImage(athlete.avatarUrl);
  if (customImage) return customImage;
  return loadShareImage(defaultRankingAvatarUrl(athlete.sex));
}

function fitText(
  context: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxSize: number,
  minSize: number,
  weight = 900,
) {
  let size = maxSize;
  do {
    context.font = `${weight} ${size}px system-ui, -apple-system, BlinkMacSystemFont, sans-serif`;
    if (context.measureText(text).width <= maxWidth) return size;
    size -= 2;
  } while (size >= minSize);
  return minSize;
}

function truncateCanvasText(
  context: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
) {
  const safeText = String(text || "").trim();
  if (!safeText || context.measureText(safeText).width <= maxWidth) {
    return safeText;
  }
  let truncated = safeText;
  while (
    truncated.length > 1 &&
    context.measureText(`${truncated.trimEnd()}...`).width > maxWidth
  ) {
    truncated = truncated.slice(0, -1);
  }
  return `${truncated.trimEnd()}...`;
}

function wrapCanvasText(
  context: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
) {
  const words = String(text || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const lines: string[] = [];
  let currentLine = "";

  words.forEach((word) => {
    const nextLine = currentLine ? `${currentLine} ${word}` : word;
    if (!currentLine || context.measureText(nextLine).width <= maxWidth) {
      currentLine = nextLine;
      return;
    }
    lines.push(currentLine);
    currentLine = word;
  });

  if (currentLine) lines.push(currentLine);
  return lines;
}

function drawWrappedCanvasText({
  context,
  text,
  x,
  y,
  maxWidth,
  maxSize,
  minSize,
  fillStyle,
  align = "left",
  weight = 900,
}: {
  context: CanvasRenderingContext2D;
  text: string;
  x: number;
  y: number;
  maxWidth: number;
  maxSize: number;
  minSize: number;
  fillStyle: string | CanvasGradient;
  align?: CanvasTextAlign;
  weight?: number;
}) {
  const safeText = String(text || "").trim();
  if (!safeText) return;

  let size = maxSize;
  let lines: string[] = [];
  do {
    context.font = `${weight} ${size}px system-ui, -apple-system, BlinkMacSystemFont, sans-serif`;
    lines = wrapCanvasText(context, safeText, maxWidth);
    if (lines.length <= 4) break;
    size -= 2;
  } while (size >= minSize);

  context.textAlign = align;
  context.fillStyle = fillStyle;
  lines.forEach((line, index) => {
    context.fillText(line, x, y + index * Math.round(size * 1.15));
  });
}

function drawFitText({
  context,
  text,
  x,
  y,
  maxWidth,
  maxSize,
  minSize,
  fillStyle,
  align = "left",
  weight = 900,
}: {
  context: CanvasRenderingContext2D;
  text: string;
  x: number;
  y: number;
  maxWidth: number;
  maxSize: number;
  minSize: number;
  fillStyle: string | CanvasGradient;
  align?: CanvasTextAlign;
  weight?: number;
}) {
  fitText(context, text, maxWidth, maxSize, minSize, weight);
  context.textAlign = align;
  context.fillStyle = fillStyle;
  context.fillText(truncateCanvasText(context, text, maxWidth), x, y);
}

function drawRankBadge(
  context: CanvasRenderingContext2D,
  position: number,
  x: number,
  y: number,
) {
  const colors: Record<number, string> = {
    1: "#e7c565",
    2: "#c7d0d8",
    3: "#c98a5d",
  };
  const color = colors[position] || "rgba(150,174,194,0.78)";
  context.save();
  context.strokeStyle = color;
  context.lineWidth = 3;
  context.beginPath();
  context.arc(x, y, 34, 0, Math.PI * 2);
  context.stroke();
  context.fillStyle = color;
  context.font =
    "900 30px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.fillText(String(position), x, y + 1);
  context.restore();
}

function drawAvatar({
  context,
  image,
  name,
  x,
  y,
  radius,
  blue,
  pink,
}: {
  context: CanvasRenderingContext2D;
  image: HTMLImageElement | null;
  name: string;
  x: number;
  y: number;
  radius: number;
  blue: string;
  pink: string;
}) {
  context.save();
  context.beginPath();
  context.arc(x, y, radius, 0, Math.PI * 2);
  context.closePath();
  context.clip();
  if (image) {
    const side = Math.min(image.width, image.height);
    context.drawImage(
      image,
      (image.width - side) / 2,
      (image.height - side) / 2,
      side,
      side,
      x - radius,
      y - radius,
      radius * 2,
      radius * 2,
    );
  } else {
    const gradient = context.createLinearGradient(
      x - radius,
      y - radius,
      x + radius,
      y + radius,
    );
    gradient.addColorStop(0, blue);
    gradient.addColorStop(1, pink);
    context.fillStyle = gradient;
    context.fillRect(x - radius, y - radius, radius * 2, radius * 2);
    context.fillStyle = "#ffffff";
    context.font = `900 ${Math.max(20, radius * 0.72)}px system-ui, -apple-system, BlinkMacSystemFont, sans-serif`;
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(initialsFromName(name), x, y + 1);
  }
  context.restore();

  context.strokeStyle = "rgba(255,255,255,0.72)";
  context.lineWidth = 4;
  context.beginPath();
  context.arc(x, y, radius, 0, Math.PI * 2);
  context.stroke();
}

function drawRankingRow({
  context,
  athlete,
  image,
  y,
  strong,
  soft,
  blue,
  pink,
}: {
  context: CanvasRenderingContext2D;
  athlete: ShareAthlete;
  image: HTMLImageElement | null;
  y: number;
  strong: string;
  soft: string;
  blue: string;
  pink: string;
}) {
  drawRankBadge(context, athlete.position, 142, y);
  drawAvatar({
    context,
    image,
    name: athlete.playerName,
    x: 230,
    y,
    radius: 36,
    blue,
    pink,
  });
  drawFitText({
    context,
    text: athlete.playerName,
    x: 292,
    y: y + 11,
    maxWidth: 455,
    maxSize: 28,
    minSize: 20,
    fillStyle: strong,
  });
  context.textAlign = "right";
  context.fillStyle = "#ffffff";
  context.font =
    "900 32px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.fillText(
    truncateCanvasText(context, String(athlete.points), 116),
    878,
    y + 11,
  );
  context.fillStyle = soft;
  context.font =
    "600 18px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.fillText(
    siteTexts.ranking.shareModal.canvas.pointsSuffix,
    935,
    y + 11,
  );
}

async function createRankingShareBlob({
  rankingName,
  categoryLabel,
  selectionLabel,
  topRows,
  visualIdentity,
}: {
  rankingName: string;
  categoryLabel: string;
  selectionLabel?: string;
  topRows: ShareAthlete[];
  visualIdentity?: OrganizerVisualIdentity | null;
}) {
  const canvas = document.createElement("canvas");
  canvas.width = 1080;
  canvas.height = 1920;
  const context = canvas.getContext("2d");
  if (!context)
    throw new Error(siteTexts.ranking.shareModal.canvas.unavailableCanvas);

  const styles = getComputedStyle(document.documentElement);
  const blue =
    styles.getPropertyValue("--color-brand-blue").trim() || "#2b9cdf";
  const pink =
    styles.getPropertyValue("--color-brand-pink").trim() || "#e53682";
  const identityColors = getEffectiveIdentityColors(visualIdentity);
  const primary = visualIdentity?.mode === "custom" ? identityColors.primaryColor : blue;
  const secondary =
    visualIdentity?.mode === "custom" ? identityColors.secondaryColor : pink;
  const strong =
    styles.getPropertyValue("--color-brand-white").trim() || "#ffffff";
  const soft = "rgba(222,235,245,0.82)";

  const [logoImage, organizerLogoImage, topImages] = await Promise.all([
    loadShareImage(brandLogo),
    loadShareImage(visualIdentity?.logoUrl || ""),
    Promise.all(topRows.map((row) => loadRankingAvatarImage(row))),
  ]);

  const bg = context.createLinearGradient(0, 0, 1080, 1920);
  bg.addColorStop(0, "#020817");
  bg.addColorStop(0.55, "#06142b");
  bg.addColorStop(1, "#110018");
  context.fillStyle = bg;
  context.fillRect(0, 0, 1080, 1920);

  context.strokeStyle = primary;
  context.lineWidth = 2;
  context.globalAlpha = 0.72;
  context.beginPath();
  context.arc(820, 40, 430, 0.25, 1.9);
  context.stroke();
  context.strokeStyle = secondary;
  context.beginPath();
  context.arc(870, 1730, 620, 3.6, 5.5);
  context.stroke();
  context.globalAlpha = 1;

  context.fillStyle = "rgba(255,255,255,0.035)";
  context.strokeStyle = "rgba(132,166,198,0.34)";
  context.lineWidth = 2;
  context.beginPath();
  context.roundRect(72, 92, 936, 1540, 28);
  context.fill();
  context.stroke();

  if (organizerLogoImage) {
    const ratio = Math.min(
      150 / organizerLogoImage.width,
      106 / organizerLogoImage.height,
    );
    const drawWidth = organizerLogoImage.width * ratio;
    const drawHeight = organizerLogoImage.height * ratio;
    context.drawImage(
      organizerLogoImage,
      112 + (150 - drawWidth) / 2,
      140 + (106 - drawHeight) / 2,
      drawWidth,
      drawHeight,
    );
  } else if (logoImage) {
    context.drawImage(logoImage, 112, 140, 150, 106);
  } else {
    const logoGradient = context.createLinearGradient(112, 150, 265, 210);
    logoGradient.addColorStop(0, primary);
    logoGradient.addColorStop(1, secondary);
    context.fillStyle = logoGradient;
    context.font =
      "italic 900 78px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
    context.fillText("TNP", 112, 205);
  }

  drawWrappedCanvasText({
    context,
    text: formatRankingShareTitle(categoryLabel, selectionLabel),
    x: 948,
    y: 158,
    maxWidth: 640,
    maxSize: 40,
    minSize: 28,
    fillStyle: strong,
    align: "right",
  });

  context.fillStyle = soft;
  context.font =
    "900 28px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.textAlign = "left";
  context.fillText("Top 10", 112, 360);

  topRows.forEach((row, index) => {
    const y = 440 + index * 104;
    drawRankingRow({
      context,
      athlete: row,
      image: topImages[index] || null,
      y,
      strong,
      soft,
      blue: primary,
      pink: secondary,
    });
    if (index < topRows.length - 1) {
      context.strokeStyle = "rgba(132,166,198,0.18)";
      context.beginPath();
      context.moveTo(112, y + 54);
      context.lineTo(948, y + 54);
      context.stroke();
    }
  });

  const footerGradient = context.createLinearGradient(172, 1510, 548, 1510);
  footerGradient.addColorStop(0, blue);
  footerGradient.addColorStop(1, pink);
  context.fillStyle = footerGradient;
  context.font =
    "700 30px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.textAlign = "left";
  context.fillText(
    truncateCanvasText(context, "www.todosnopadel.com.br", 520),
    172,
    1510,
  );
  return blobFromCanvas(canvas);
}

export default function RankingShareModal({
  open,
  rankingName,
  categoryLabel,
  selectionLabel,
  rankingRows,
  shareUrl,
  visualIdentity,
  onClose,
}: RankingShareModalProps) {
  const [preview, setPreview] = useState<SharePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  useSiteLanguage();
  const t = siteTexts.ranking.shareModal;
  const info = siteTexts.common.info;

  const topRows = useMemo<ShareAthlete[]>(
    () =>
      rankingRows.slice(0, 10).map((row) => ({
        position: Number(row.position || 0),
        playerName: String(row.playerName || t.athleteFallback),
        points: Number(row.points || 0),
        avatarUrl: row.avatarUrl,
        sex: row.sex || "",
      })),
    [rankingRows, t.athleteFallback],
  );

  const filename = useMemo(
    () =>
      `ranking-tnp-${selectionLabel || `${rankingName}-${categoryLabel}`}`
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "") + ".png",
    [categoryLabel, rankingName, selectionLabel],
  );

  const createPreview = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    setStatus("");
    try {
      const blob = await createRankingShareBlob({
        rankingName,
        categoryLabel,
        selectionLabel,
        topRows,
        visualIdentity,
      });
      setPreview((current) => {
        if (current?.url) URL.revokeObjectURL(current.url);
        return { blob, filename, url: URL.createObjectURL(blob) };
      });
    } catch (_error) {
      setStatus(t.generateError);
    } finally {
      setLoading(false);
    }
  }, [
    categoryLabel,
    filename,
    loading,
    rankingName,
    selectionLabel,
    topRows,
    t.generateError,
    visualIdentity,
  ]);

  useEffect(() => {
    if (!open) return;
    void createPreview();
  }, [createPreview, open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, open]);

  useEffect(() => {
    if (open) return;
    setPreview((current) => {
      if (current?.url) URL.revokeObjectURL(current.url);
      return null;
    });
    setStatus("");
  }, [open]);

  const shareNative = useCallback(
    async () => {
      if (!preview) return;
      const file = new File([preview.blob], preview.filename, {
        type: "image/png",
      });
      const payload = {
        files: [file],
        title: t.shareTitle,
        text: `${t.shareTextPrefix} ${
          selectionLabel || `${rankingName} - ${categoryLabel}`
        }: ${shareUrl}`,
      };
      if (navigator.share && navigator.canShare?.(payload)) {
        await navigator.share(payload);
        return;
      }
      downloadBlob(preview.blob, preview.filename);
      setStatus(t.directUnavailable);
    },
    [
      categoryLabel,
      preview,
      rankingName,
      selectionLabel,
      shareUrl,
      t.directUnavailable,
      t.shareTextPrefix,
      t.shareTitle,
    ],
  );

  const downloadPreview = useCallback(() => {
    if (!preview) return;
    downloadBlob(preview.blob, preview.filename);
  }, [preview]);

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setStatus(t.copiedLink);
    } catch (_error) {
      setStatus(t.copyError);
    }
  }, [shareUrl, t.copiedLink, t.copyError]);

  useBodyScrollLock(open);

  if (!open) return null;

  return (
    <div
      className="ranking-share-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={t.title}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="ranking-share-modal">
        <div className="ranking-share-header">
          <div>
            <strong className="info-label">
              {t.title}
              <InfoTooltip content={info.shareRanking} />
            </strong>
            <span>
              {selectionLabel || `${rankingName} - ${categoryLabel}`}
            </span>
          </div>
          <button
            type="button"
            className="ranking-share-close"
            onClick={onClose}
            aria-label={t.closeAria}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              close
            </span>
          </button>
        </div>

        <div className="ranking-share-content">
          <div className="ranking-share-preview">
            {preview ? (
              <img src={preview.url} alt={t.previewAlt} />
            ) : (
              <div className="ranking-share-loading">
                {loading ? t.generatingImage : t.imageUnavailable}
              </div>
            )}
          </div>
          <div className="ranking-share-actions">
            <Button
              type="button"
              variant="primary"
              onClick={() => void shareNative()}
              disabled={!preview}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                share
              </span>
              {t.shareAction}
            </Button>
            <Button
              type="button"
              variant="pill"
              onClick={downloadPreview}
              disabled={!preview}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                download
              </span>
              {t.downloadImage}
            </Button>
            <Button type="button" variant="pill" onClick={copyLink}>
              <span className="material-symbols-outlined" aria-hidden="true">
                content_copy
              </span>
              {t.copyLink}
            </Button>
            {status && <p className="ranking-share-status">{status}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
